import type { AdminAuthErrorCode } from '@/contracts';

import type { AdminApiResult } from './api';

/**
 * Where a result object becomes an exception, and the only place it should.
 *
 * `lib/api.ts` answers with `{ ok, data } | { ok, error }` because its direct callers —
 * the login form, the shell's badges — each have a real answer for a failure. A
 * `queryFn` does not: TanStack Query's retry, `isError` and `error` all run on a thrown
 * value, and a `queryFn` that returns `null` on failure switches every one of them off
 * and caches the failure as a successful empty answer for the stale time (review RV-B4).
 * `unwrap` is the one line that turns the result back into the exception the query
 * layer is built around.
 *
 * Its own module rather than an export of `api.ts`, because the specs replace that
 * module wholesale with `vi.mock('@/lib/api', …)` and would each have to re-export it.
 */
type ApiFailure = Extract<AdminApiResult<never>, { ok: false }>;

export class ApiError extends Error {
  /** The API's stable code — `unavailable` for a request that never got an answer. */
  readonly code: AdminAuthErrorCode;
  /** The HTTP status, when there was a response at all. */
  readonly status: number | undefined;
  readonly retryAfterSeconds: number | undefined;

  constructor(failure: ApiFailure) {
    super(
      failure.status === undefined
        ? `API request failed: ${failure.error}`
        : `API request failed with ${failure.status}: ${failure.error}`,
    );
    this.name = 'ApiError';
    this.code = failure.error;
    this.status = failure.status;
    this.retryAfterSeconds = failure.retryAfterSeconds;
  }

  /**
   * A read of a record that does not exist. The one failure a detail screen renders as
   * a state of its own — "no such talent" with a way back — rather than as an error.
   */
  get notFound(): boolean {
    return this.status === 404;
  }

  /**
   * The request itself was refused: a 4xx. Sending it again unchanged gets the same
   * answer, so the query client does not retry these.
   */
  get clientError(): boolean {
    return this.status !== undefined && this.status >= 400 && this.status < 500;
  }
}

/** The data of a successful result; a thrown `ApiError` otherwise. */
export function unwrap<T>(result: AdminApiResult<T>): T {
  if (result.ok) return result.data;
  throw new ApiError(result);
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Whether a query's `error` is a 404 — for the screens that have a not-found state. */
export function isNotFound(error: unknown): boolean {
  return isApiError(error) && error.notFound;
}
